/**
 * 계산 함수의 **화면용 래퍼 셋**.
 *
 * 편집기가 금액을 세는 자리가 셋이다 — 합계 패널 · 공종 카드 · 항목 행. 하나라도
 * 맨몸이면 자릿수를 잘못 친 순간 그 자리가 던져 **편집기가 통째로 언마운트**된다.
 *
 * 계산 규칙(절사액 > 총계면 거부)은 그대로 둔다 — 저장까지 새어 나가면 안 되는 값이다.
 * 다만 **편집기는 렌더 중에** 금액을 계산하므로, 던지는 순간 편집기가 통째로
 * 언마운트되어 미저장 편집이 전부 사라진다. 화면은 값 대신 이 결과를 받아
 * 경고를 띄우고 저장만 막는다.
 */

import {
  calcItem,
  CalcItemInput,
  CalcItemResult,
  calcQuote,
  CalcQuoteInput,
  CalcQuoteResult,
  calcTrade,
  CalcTradeInput,
  CalcTradeResult,
} from "./calc";

export type SafeCalcResult =
  | { ok: true; result: CalcQuoteResult }
  | { ok: false; message: string };

export const safeCalcQuote = (input: CalcQuoteInput): SafeCalcResult => {
  try {
    return { ok: true, result: calcQuote(input) };
  } catch (error) {
    return {
      ok: false,
      message:
        error instanceof Error
          ? error.message
          : "금액을 계산할 수 없습니다 — 입력을 확인해 주세요",
    };
  }
};

/**
 * 공종 한 줄의 래퍼. **공종 카드가 렌더 중에 따로 계산하기 때문에** 필요하다.
 *
 * `safeCalcQuote` 만 있으면 부족했다 — 합계 패널은 감싸져 있는데 공종 카드는
 * `calcTrade` 를 맨몸으로 불러, 금액이 셀 수 있는 범위를 넘는 순간 그 카드가
 * 던져 **편집기가 통째로 언마운트**되고 미저장 편집이 사라졌다.
 *
 * 못 세면 0 원으로 보여 준다 — 합계 패널이 같은 입력으로 이미 경고를 띄우므로
 * 여기서 또 말할 필요가 없고, 카드가 사라지는 것보다 금액 칸이 0 인 편이 낫다.
 */
export const safeCalcTrade = (input: CalcTradeInput): CalcTradeResult => {
  try {
    return calcTrade(input);
  } catch {
    return {
      group: input.group,
      items: [],
      material_amount: 0,
      labor_amount: 0,
      amount: 0,
      cost_amount: 0,
      cost_missing_count: 0,
    };
  }
};

/**
 * 항목 한 줄의 래퍼. 행마다 렌더 중에 계산하므로 여기도 던지면 안 된다.
 *
 * 화면에서 금액을 세는 자리는 셋이다 — 합계 패널(`safeCalcQuote`) · 공종 카드
 * (`safeCalcTrade`) · 항목 행(여기). **하나라도 맨몸이면 자릿수를 잘못 친 순간
 * 편집기가 통째로 사라진다.**
 */
export const safeCalcItem = (input: CalcItemInput): CalcItemResult => {
  try {
    return calcItem(input);
  } catch {
    return {
      is_set: (input.children ?? []).length > 0,
      children: [],
      material_amount: 0,
      labor_amount: 0,
      amount: 0,
      cost_amount: null,
      cost_missing_count: 0,
    };
  }
};
